import { PortalErrorNotice } from '@/components/common/portal-error-notice';
import { portalFeatureAvailabilitySchema, type PortalFeatureAvailabilityDto } from '@erp/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { subscribeToEventStream } from '@/lib/event-stream';
import { portalFetch } from '@/lib/portal-api';
import { PortalFeatureContext, usePortalFeatureAvailability } from './portal-feature-context';

const QUERY_KEY = ['portal', 'features'] as const;
function usePortalFeaturesQuery() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () =>
      portalFetch('/api/v1/portal/features/effective', portalFeatureAvailabilitySchema),
    staleTime: 10_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function PortalFeatureAvailabilityProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const { t } = useTranslation();
  const query = usePortalFeaturesQuery();
  const queryClient = useQueryClient();
  useEffect(
    () =>
      subscribeToEventStream({
        path: '/api/v1/portal/features/stream',
        events: ['feature-availability'],
        onEvent: () => void queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
        ensureAuthenticated: async () => {
          try {
            await portalFetch('/api/v1/portal/features/effective', portalFeatureAvailabilitySchema);
            return true;
          } catch {
            return false;
          }
        },
      }),
    [queryClient],
  );
  if (query.isError) {
    return (
      <Card role="alert" className="m-6">
        <CardHeader>
          <PortalErrorNotice error={query.error} />
        </CardHeader>
        <CardContent>
          <Button type="button" variant="outline" onClick={() => void query.refetch()}>
            {t('common.retry')}
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (query.data === undefined) {
    return (
      <div className="space-y-4 p-6" aria-busy="true">
        <Skeleton className="h-10 w-56" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  return (
    <PortalFeatureContext.Provider value={query.data}>{children}</PortalFeatureContext.Provider>
  );
}

function portalPathEnabled(pathname: string, state: PortalFeatureAvailabilityDto): boolean {
  if (pathname === '/portal' || pathname === '/portal/') return state.documents;
  if (
    pathname.startsWith('/portal/documents') ||
    pathname.startsWith('/portal/dunning') ||
    pathname.startsWith('/portal/files')
  ) {
    return state.documents;
  }
  if (pathname.startsWith('/portal/catalog') || pathname.startsWith('/portal/cart')) {
    return state.orders;
  }
  return state.customerPortal;
}

export function PortalFeatureRouteBoundary({ children }: { children: ReactNode }): JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const state = usePortalFeatureAvailability();
  if (portalPathEnabled(location.pathname, state)) return <>{children}</>;
  return (
    <Card role="status" className="mx-auto max-w-2xl">
      <CardHeader>
        <Ban aria-hidden="true" className="mb-2 size-8 text-muted-foreground" />
        <CardTitle>{t('portal.featureDisabled.title')}</CardTitle>
        <CardDescription>{t('portal.featureDisabled.description')}</CardDescription>
        <PortalErrorNotice error={state} kind="mode" />
      </CardHeader>
    </Card>
  );
}
